<template>
  <div class="page-container">
    <PageHeader
      title="Track Applications"
      subtitle="Type an application number to see where it is and everything that has happened to it."
      icon="mdi-map-marker-path"
    />

    <!-- Search -->
    <form class="tr-search" @submit.prevent="search()">
      <v-text-field
        v-model.trim="query"
        outlined
        hide-details
        clearable
        autofocus
        dense
        class="tr-search-input"
        prepend-inner-icon="mdi-magnify"
        placeholder="Application number, e.g. KFD/FS/1024/2026"
        :disabled="loading"
        @click:clear="reset"
      ></v-text-field>
      <v-btn type="submit" depressed height="40" class="btn-act btn-act--primary px-6" :loading="loading" :disabled="!query">
        <v-icon small left>mdi-magnify</v-icon>
        Search
      </v-btn>
    </form>

    <!-- Nothing searched yet -->
    <div v-if="!searched && !loading" class="tr-state">
      <span class="tr-state-icon"><v-icon size="34" color="#15803d">mdi-file-search-outline</v-icon></span>
      <div class="tr-state-title">Find an application</div>
      <div class="tr-state-text">Enter the full application number and press Search. Part of a number shows the closest matches.</div>
    </div>

    <!-- Searching -->
    <div v-else-if="loading" class="tr-state">
      <v-progress-circular indeterminate color="#15803d" size="44" width="4"></v-progress-circular>
      <div class="tr-state-text">Looking up {{ lastQuery }}…</div>
    </div>

    <!-- Not found -->
    <div v-else-if="!app" class="tr-state">
      <span class="tr-state-icon tr-state-icon--warn"><v-icon size="34" color="#b45309">mdi-file-question-outline</v-icon></span>
      <div class="tr-state-title">{{ message || "No application found." }}</div>
      <template v-if="suggestions.length">
        <div class="tr-state-text">Did you mean one of these?</div>
        <div class="tr-suggest">
          <button v-for="s in suggestions" :key="s._id" type="button" class="tr-suggest-item" @click="search(s.applicationNo)">
            <span class="app-no">{{ s.applicationNo }}</span>
            <span class="tr-suggest-text">
              <b>{{ s.applicantName || "—" }}</b>
              <span>{{ s.natureOfApplication }} · {{ formatDate(s.created_at) }}</span>
            </span>
            <v-icon small color="#94a3b8">mdi-chevron-right</v-icon>
          </button>
        </div>
      </template>
      <div v-else class="tr-state-text">Check the number and try again.</div>
    </div>

    <!-- Found -->
    <template v-else>
      <section class="tr-card">
        <div class="tr-card-head">
          <div class="tr-card-id">
            <span class="app-no">{{ app.applicationNo }}</span>
            <span class="status-pill" :class="'status-pill--' + status.tone">{{ status.label }}</span>
            <span v-if="app.originalApplication && app.originalApplication.applicationNo" class="tr-ext">
              Extension of {{ app.originalApplication.applicationNo }}
            </span>
          </div>
          <v-btn depressed class="btn-act btn-act--primary" @click="openApp">
            <v-icon small left>mdi-open-in-new</v-icon>
            Open application
          </v-btn>
        </div>

        <!-- Where it is now -->
        <div class="tr-now" :class="'tr-now--' + where.tone">
          <span class="tr-now-icon"><v-icon>{{ where.icon }}</v-icon></span>
          <div>
            <div class="tr-now-label">Currently with</div>
            <div class="tr-now-who">{{ where.who }}</div>
            <div class="tr-now-detail">{{ [where.stage, where.detail].filter(Boolean).join(" · ") }}</div>
          </div>
        </div>

        <div class="tr-grid">
          <div class="tr-field">
            <span class="tr-label">Applicant</span>
            <span class="tr-value">{{ app.applicantName || "—" }}</span>
            <span class="tr-sub">{{ [app.applicantMobileNo, app.applicantEmail].filter(Boolean).join(" · ") }}</span>
          </div>
          <div class="tr-field">
            <span class="tr-label">Category</span>
            <span class="tr-value">{{ app.natureOfApplication || "—" }}</span>
            <span v-if="app.nameofProductionhouse" class="tr-sub">{{ app.nameofProductionhouse }}</span>
          </div>
          <div class="tr-field">
            <span class="tr-label">Division</span>
            <span class="tr-value">{{ (app.divisionid && app.divisionid.officeName) || "—" }}</span>
          </div>
          <div class="tr-field">
            <span class="tr-label">Submitted</span>
            <span class="tr-value">{{ formatDate(app.created_at) }}</span>
            <span class="tr-sub">{{ fromNow(app.created_at) }}</span>
          </div>
          <div class="tr-field">
            <span class="tr-label">Fees</span>
            <span class="tr-value">₹{{ money(app.shootingfee) }}</span>
            <span class="tr-sub">
              {{ ["Deposit ₹" + money(app.securitydeposit), app.installationfee ? "Installation ₹" + money(app.installationfee) : ""].filter(Boolean).join(" · ") }}
            </span>
          </div>
          <div class="tr-field">
            <span class="tr-label">Payment</span>
            <span class="tr-value">{{ app.paymentStatus === "Success" ? "Paid" : app.paymentInitiation === "YES" ? "Awaiting payment" : "Not requested yet" }}</span>
            <span v-if="app.licenseNo" class="tr-sub">Licence {{ app.licenseNo }}</span>
          </div>
        </div>

        <!-- Shooting locations -->
        <div v-if="shoots.length" class="tr-shoots">
          <div class="tr-label">Shooting {{ shoots.length > 1 ? "locations" : "location" }}</div>
          <div v-for="(s, i) in shoots" :key="i" class="tr-shoot">
            <v-icon small color="#15803d">mdi-movie-open-outline</v-icon>
            <div>
              <b>{{ s.filmName || "Untitled" }}</b>
              <span>{{ [s.location, s.rangeName].filter(Boolean).join(" · ") }}</span>
            </div>
            <span class="tr-shoot-dates">{{ formatDate(s.fromDate) }} – {{ formatDate(s.toDate) }}</span>
          </div>
        </div>
      </section>

      <!-- Timeline -->
      <section class="tr-card">
        <div class="tr-section-title">
          <v-icon small color="#15803d">mdi-timeline-clock-outline</v-icon>
          Timeline
          <span class="tr-count">{{ records.length }} step{{ records.length === 1 ? "" : "s" }}</span>
        </div>
        <TimelineView v-if="records.length" :records="records" :application="app" />
        <div v-else class="tr-empty">No one has acted on this application yet.</div>
      </section>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader.vue";
import TimelineView, { journeyRecords } from "@/components/Common/TimelineView.vue";
import { currentlyWith, statusOf } from "@/components/Common/deskConfig";

// Track Applications (admin and DFO): look up one application by its number
// and show its details, who holds it now and its full timeline. The number is
// kept in the address (?no=) so a result can be reloaded or shared.
export default {
  name: "TrackApplication",
  components: { PageHeader, TimelineView },
  data() {
    return {
      query: "",
      lastQuery: "",
      loading: false,
      searched: false,
      app: null,
      records: [],
      message: "",
      suggestions: [],
    };
  },
  computed: {
    status() {
      return statusOf(this.app);
    },
    where() {
      return currentlyWith(this.app);
    },
    shoots() {
      return (this.app && this.app.filmShootingDetails) || [];
    },
  },
  created() {
    const no = this.$route.query.no;
    if (no) this.search(String(no));
  },
  methods: {
    search(number) {
      const text = String(number || this.query || "").trim();
      if (!text) return;
      this.query = text;
      this.lastQuery = text;
      this.loading = true;
      this.searched = true;
      if (this.$route.query.no !== text) {
        this.$router.replace({ query: { ...this.$route.query, no: text } }).catch(() => {});
      }
      axios({
        method: "POST",
        url: "/track/application",
        headers: { token: localStorage.getItem("token") },
        data: { applicationNo: text },
      })
        .then((response) => {
          this.loading = false;
          const res = response.data || {};
          if (res.status && res.data) {
            this.app = res.data;
            this.records = journeyRecords(res.records);
            this.message = "";
            this.suggestions = [];
          } else {
            this.app = null;
            this.records = [];
            this.message = res.msg || "";
            this.suggestions = res.suggestions || [];
          }
        })
        .catch((err) => {
          this.loading = false;
          this.app = null;
          this.records = [];
          this.suggestions = [];
          this.message = "Could not look up the application. Please try again.";
          console.error("Error tracking application:", err);
        });
    },
    reset() {
      this.query = "";
      this.searched = false;
      this.app = null;
      this.records = [];
      this.message = "";
      this.suggestions = [];
      if (this.$route.query.no) {
        const query = { ...this.$route.query };
        delete query.no;
        this.$router.replace({ query }).catch(() => {});
      }
    },
    openApp() {
      if (this.app) this.$router.push("/AdminFilmDetailedView?id=" + this.app._id);
    },
    formatDate(value) {
      return value ? moment(value).format("DD MMM YYYY") : "—";
    },
    fromNow(value) {
      return value ? moment(value).fromNow() : "";
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
  },
};
</script>

<style scoped>
.tr-search {
  display: flex;
  gap: 10px;
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e3eee6;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.tr-search-input {
  flex: 1;
}

.tr-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px dashed #d7ebdd;
  text-align: center;
}

.tr-state-icon {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(21, 128, 61, 0.1);
}

.tr-state-icon--warn {
  background: #fef3c7;
}

.tr-state-title {
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
}

.tr-state-text {
  max-width: 460px;
  font-size: 14px;
  color: #64748b;
}

.tr-suggest {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 560px;
  margin-top: 6px;
}

.tr-suggest-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e3eee6;
  background: #fbfdfb;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.tr-suggest-item:hover,
.tr-suggest-item:focus-visible {
  border-color: #15803d;
  background: #f3faf5;
  outline: none;
}

.tr-suggest-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 12.5px;
  color: #64748b;
}

.tr-suggest-text b {
  font-size: 14px;
  color: #0f172a;
}

.tr-card {
  padding: 20px 24px;
  margin-bottom: 20px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e3eee6;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.tr-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.tr-card-id {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.tr-card-id .app-no {
  font-size: 15px;
}

.tr-ext {
  font-size: 12.5px;
  font-weight: 700;
  color: #64748b;
}

.tr-now {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 18px 0;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f3faf5;
  border: 1px solid #d7ebdd;
}

.tr-now-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #15803d;
}

.tr-now-icon .v-icon {
  color: #ffffff !important;
}

.tr-now--applicant {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.tr-now--applicant .tr-now-icon {
  background: #1d4ed8;
}

.tr-now--range {
  background: #fffbeb;
  border-color: #fde68a;
}

.tr-now--range .tr-now-icon {
  background: #b45309;
}

.tr-now--closed {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.tr-now--closed .tr-now-icon {
  background: #64748b;
}

.tr-now-label {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #64748b;
}

.tr-now-who {
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
}

.tr-now-detail {
  font-size: 13px;
  color: #475569;
}

.tr-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 24px;
}

.tr-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tr-label {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #64748b;
}

.tr-value {
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
}

.tr-sub {
  font-size: 12.5px;
  color: #64748b;
  word-break: break-word;
}

.tr-shoots {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px dashed #e3eee6;
}

.tr-shoot {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fbfdfb;
  border: 1px solid #e3eee6;
}

.tr-shoot > div {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 12.5px;
  color: #64748b;
}

.tr-shoot b {
  font-size: 14px;
  color: #0f172a;
}

.tr-shoot-dates {
  font-size: 13px;
  font-weight: 700;
  color: #14532d;
  white-space: nowrap;
}

.tr-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.tr-count {
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(21, 128, 61, 0.1);
  color: #15803d;
  font-size: 12px;
  font-weight: 700;
}

.tr-empty {
  padding: 24px 0;
  text-align: center;
  color: #64748b;
  font-weight: 600;
}

@media (max-width: 960px) {
  .tr-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .tr-search {
    flex-direction: column;
    padding: 12px;
    border-radius: 14px;
  }

  .tr-card {
    padding: 16px;
    border-radius: 14px;
  }

  .tr-card-head .v-btn {
    width: 100%;
  }

  .tr-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .tr-shoot {
    flex-wrap: wrap;
  }

  .tr-shoot-dates {
    width: 100%;
    padding-left: 26px;
  }
}
</style>
